/**
 * Parameters Step (Wizard step 4).
 *
 * One editable table of input_schema parameters (the columns the LLM polisher
 * exposes to end-user questions). Seeded from auto-inference / an AI proposal;
 * users edit any cell inline and add/remove rows.
 *
 * "Suggest parameters with AI" calls a swappable ParameterAdvisor. If the table
 * already has data, a confirmation modal gates the replace to avoid silently
 * wiping user edits (propose-confirm pattern — mirrors MetricsStep).
 *
 * Form fields only — no API, no AWS.
 */

import React, { useRef, useState } from 'react';
import {
  SpaceBetween,
  Container,
  Header,
  Table,
  Button,
  Input,
  Textarea,
  Select,
  Toggle,
  Box,
  Alert,
  Modal,
  StatusIndicator,
} from '@amzn/awsui-components-react';

import {
  ParamType,
  ParameterAdvisor,
  ParameterDraft,
  ParameterSuggestion,
  OnboardingConfig,
  ColumnInfo,
} from '../types';
import { OnboardingAction, ParameterField } from '../onboardingReducer';
import { heuristicParameterAdvisor } from '../parameterAdvisor';

// ─── Single source of truth for ParamType dropdown options ────────────
const PARAM_TYPE_VALUES: readonly ParamType[] = ['string', 'number', 'boolean', 'array'];
const PARAM_TYPE_OPTIONS: ReadonlyArray<{ label: string; value: ParamType }> =
  PARAM_TYPE_VALUES.map((v) => ({ label: v, value: v }));

export interface ParametersStepProps {
  config: OnboardingConfig;
  columns?: ColumnInfo[];
  dispatch: React.Dispatch<OnboardingAction>;
  /** Override the default heuristicParameterAdvisor (for testing / the LLM advisor). */
  advisor?: ParameterAdvisor;
}

// ─── Parameters table ────────────────────────────────────────────────────

function parameterColumns(dispatch: React.Dispatch<OnboardingAction>) {
  const update = (
    index: number,
    field: ParameterField,
    value: string | boolean | string[] | null,
  ) => dispatch({ type: 'UPDATE_PARAMETER', index, field, value });

  return [
    {
      id: 'parameterName',
      header: 'Parameter name',
      width: 150,
      cell: (item: ParameterDraft & { _index: number }) => (
        <Input
          value={item.parameterName}
          onChange={({ detail }) => update(item._index, 'parameterName', detail.value)}
          ariaLabel={`Parameter ${item._index + 1} name`}
        />
      ),
    },
    {
      id: 'type',
      header: 'Type',
      width: 100,
      cell: (item: ParameterDraft & { _index: number }) => (
        <Select
          selectedOption={{ label: item.type, value: item.type }}
          options={PARAM_TYPE_OPTIONS as Array<{ label: string; value: string }>}
          onChange={({ detail }) =>
            update(item._index, 'type', detail.selectedOption.value as string)
          }
          expandToViewport
          ariaLabel={`Parameter ${item._index + 1} type`}
        />
      ),
    },
    {
      id: 'required',
      header: 'Required',
      width: 80,
      cell: (item: ParameterDraft & { _index: number }) => (
        <Toggle
          checked={item.required}
          onChange={({ detail }) => update(item._index, 'required', detail.checked)}
          ariaLabel={`Parameter ${item._index + 1} required`}
        />
      ),
    },
    {
      id: 'enumValues',
      header: 'Enum values (comma-separated)',
      width: 180,
      cell: (item: ParameterDraft & { _index: number }) => (
        <Input
          value={(item.enumValues ?? []).join(', ')}
          onChange={({ detail }) => {
            const trimmed = detail.value.trim();
            const parsed =
              trimmed.length === 0
                ? null
                : detail.value
                    .split(',')
                    .map((v) => v.trim())
                    .filter((v) => v.length > 0);
            update(item._index, 'enumValues', parsed);
          }}
          placeholder="e.g. US, GB, DE"
          ariaLabel={`Parameter ${item._index + 1} enum values`}
        />
      ),
    },
    {
      id: 'description',
      header: 'Description',
      cell: (item: ParameterDraft & { _index: number }) => {
        // Auto-grow: compute rows from content length and newline count so the
        // Textarea height adjusts to the LLM description's actual size. Clamped
        // 2–10 to keep empty rows compact and long descriptions bounded.
        const desc = item.description || '';
        const lineCount = (desc.match(/\n/g) || []).length + 1;
        const charLines = Math.ceil(desc.length / 80);
        const rows = Math.min(10, Math.max(2, Math.max(lineCount, charLines)));
        return (
          <Textarea
            value={item.description}
            rows={rows}
            onChange={({ detail }) => update(item._index, 'description', detail.value)}
            ariaLabel={`Parameter ${item._index + 1} description`}
          />
        );
      },
    },
    {
      id: 'actions',
      header: '',
      width: 100,
      cell: (item: ParameterDraft & { _index: number }) => (
        <Button
          variant="inline-link"
          iconName="remove"
          onClick={() => dispatch({ type: 'REMOVE_PARAMETER', index: item._index })}
          ariaLabel={`Remove parameter ${item._index + 1}`}
        >
          Remove
        </Button>
      ),
    },
  ];
}

// ─── Component ───────────────────────────────────────────────────────────

const ParametersStep: React.FC<ParametersStepProps> = ({
  config,
  columns,
  dispatch,
  advisor = heuristicParameterAdvisor,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [pendingSuggestion, setPendingSuggestion] = useState<ParameterSuggestion | null>(null);

  const items = config.parameters.map((p, i) => ({ ...p, _index: i }));

  // Mirror the latest parameters into a ref so the async handleSuggest reads the
  // CURRENT count at resolution time, not the value captured when it was invoked.
  // Without this, a row added during the (up to a minute) AI call would be
  // invisible to the closure's has-data check, bypassing the replace-confirm
  // modal and silently overwriting the manual row.
  const parametersRef = useRef(config.parameters);
  parametersRef.current = config.parameters;

  const applySuggestion = (suggestion: ParameterSuggestion) => {
    dispatch({ type: 'APPLY_SUGGESTED_PARAMETERS', suggestion });
    setWarnings(suggestion.warnings);
  };

  const handleSuggest = async () => {
    setError(null);
    setWarnings([]);
    setLoading(true);
    try {
      const suggestion = await advisor.suggest({
        columns: columns ?? [],
        businessContext: config.businessContext,
        columnNotes: config.columnNotes,
        confirmedBaseMetrics: config.baseMetrics,
        confirmedDerivedMetrics: config.derivedMetrics,
        supportsPeriodComparison: true,
      });
      if (parametersRef.current.length > 0) {
        setPendingSuggestion(suggestion);
      } else {
        applySuggestion(suggestion);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Suggestion failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SpaceBetween size="l">
      {/* Confirmation modal for non-empty table */}
      {pendingSuggestion && (
        <Modal
          visible={true}
          header="Replace existing parameters?"
          onDismiss={() => setPendingSuggestion(null)}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setPendingSuggestion(null)}>Cancel</Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    applySuggestion(pendingSuggestion);
                    setPendingSuggestion(null);
                  }}
                >
                  Replace
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          This will replace your current parameters with the AI suggestion.
        </Modal>
      )}

      {error && <Alert type="error" dismissible onDismiss={() => setError(null)}>{error}</Alert>}
      {warnings.length > 0 && (
        <Alert type="warning" dismissible onDismiss={() => setWarnings([])}>
          {warnings.join(' • ')}
        </Alert>
      )}

      <Container
        header={
          <Header
            variant="h2"
            counter={`(${config.parameters.length})`}
            description="Parameters are the filters end users can set when they ask the tool a question (e.g. country, date range). Edit, add, or remove rows."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  iconName="gen-ai"
                  onClick={handleSuggest}
                  disabled={loading}
                >
                  {loading ? (
                    <StatusIndicator type="loading">Generating with AI — this can take up to a minute…</StatusIndicator>
                  ) : (
                    'Suggest parameters with AI'
                  )}
                </Button>
                <Button
                  iconName="add-plus"
                  onClick={() => dispatch({ type: 'ADD_PARAMETER' })}
                >
                  Add parameter
                </Button>
              </SpaceBetween>
            }
          >
            Parameters
          </Header>
        }
      >
        <Table
          items={items}
          columnDefinitions={parameterColumns(dispatch)}
          variant="embedded"
          empty={
            <Box textAlign="center" color="inherit">
              No parameters yet. Suggest with AI or add one manually.
            </Box>
          }
        />
      </Container>
    </SpaceBetween>
  );
};

export default ParametersStep;
